"use client";

import type { ClipboardEvent, DragEvent, KeyboardEvent } from "react";
import { useState } from "react";
import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  HINT,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { RichTextOptionsPanel } from "@/components/tools/latex-dev/rich-text-options";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { richTextToLatex } from "@/lib/tool-kit/latex-dev/rich-text";
import {
  DEFAULT_RICH_TEXT_OPTIONS,
  type RichTextOptions,
} from "@/lib/tool-kit/latex-dev/rich-text-inline";

/** English strings. A translated content file overrides these through `ui`. */
export const RICH_TEXT_TO_LATEX_STRINGS = {
  pasteLabel: "Paste formatted text",
  pasteHint:
    "Click the box, then paste (Ctrl+V or ⌘V) from Word, Google Docs, a web page or an email. The pasted markup is read as data and never shown or run as live HTML.",
  pastePlaceholder: "Click here, then paste…",
  pasteAria: "Paste area for formatted text",
  pasted:
    "one: Read {count} character of formatted text.|other: Read {count} characters of formatted text.",
  textLabel: "Or type plain text or paste HTML code",
  textPlaceholder: "<h1>Title</h1>\n<p>Some <b>bold</b> text.</p>",
  loadExample: "Load the example",
  clear: "Clear",
  example:
    '<h1>Study notes</h1><p>Results were <b>significant</b> (p &lt; .05) for <i>both</i> groups &mdash; see <a href="https://doi.org/10.1000/182">the paper</a>. Costs: $5 &amp; 10% tax.</p><ul><li>Sample: 40 students</li><li>Method: <code>t_test</code></li></ul><table><caption>Group means</caption><tr><th>Group</th><th>Mean</th></tr><tr><td>Control</td><td>4.10</td></tr><tr><td>Treatment</td><td>5.30</td></tr></table>',
  textHeading: "Text and headings",
  schemeLabel: "Heading 1 becomes",
  schemes: {
    section: "\\section (article)",
    chapter: "\\chapter (book, report)",
  },
  unicodeLabel: "Quotes, dashes and ellipses",
  unicodeModes: {
    keep: "Keep as UTF-8 characters (“ ” – — …)",
    ligatures: "Write LaTeX ligatures (`` '' -- --- \\ldots)",
  },
  numbered: "Numbered headings (untick for \\section*)",
  fixQuotes: "Turn straight double quotes into opening and closing quotes",
  fullDocument: "Wrap in a complete document",
  tablesHeading: "Tables",
  rulesLabel: "Table rules",
  rules: {
    booktabs: "Booktabs (\\toprule, \\midrule, \\bottomrule)",
    hline: "Horizontal lines (\\hline)",
    grid: "Full grid",
    none: "No rules",
  },
  tableFloat: "Wrap tables in a table float",
  boldHeader: "Bold the header row",
  firstRowHeader: "Treat the first row as a header",
  emptyPrompt: "Paste something above, or load the example, to see the LaTeX.",
  results: "LaTeX",
  headings: "Headings",
  paragraphs: "Paragraphs",
  listItems: "List items",
  tables: "Tables",
  links: "Links",
  codeLabel: "LaTeX code",
  copyCode: "Copy LaTeX",
  copied: "Copied!",
  preambleHeading: "Add to your preamble",
  noPackages: "No extra packages needed.",
  copyPreamble: "Copy preamble lines",
  checkHeading: "Check before you compile",
  imagesNote:
    "one: {count} image was left out — a comment marks where it was. Add it with \\includegraphics.|other: {count} images were left out — comments mark where they were. Add them with \\includegraphics.",
  rowspanNote:
    "one: {count} cell spans several rows. Its text stays in the first row; use the multirow package to span it.|other: {count} cells span several rows. Their text stays in the first row; use the multirow package to span them.",
  linksNote:
    "one: {count} link had no usable web address (a script, a relative path or an in-page anchor), so only its text was kept.|other: {count} links had no usable web address (scripts, relative paths or in-page anchors), so only their text was kept.",
  deepNote:
    "one: {count} list is nested more than four deep, which LaTeX does not allow.|other: {count} lists are nested more than four deep, which LaTeX does not allow.",
  nonLatinNote:
    "These characters may stop pdfLaTeX: {chars}. Compile with XeLaTeX or LuaLaTeX, or convert them with the LaTeX to Unicode Converter.",
  allClear: "Nothing needed flagging.",
} as const;

interface Source {
  kind: "html" | "text";
  value: string;
}

const NO_SOURCE: Source = { kind: "text", value: "" };

function allowKey(event: KeyboardEvent<HTMLDivElement>): boolean {
  return event.ctrlKey || event.metaKey || event.key === "Tab";
}

export default function RichTextToLatexWidget() {
  const strings = useToolUi(RICH_TEXT_TO_LATEX_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [source, setSource] = useState<Source>(NO_SOURCE);
  const [options, setOptions] = useState<RichTextOptions>(
    DEFAULT_RICH_TEXT_OPTIONS
  );

  function take(data: DataTransfer) {
    const html = data.getData("text/html");
    const text = data.getData("text/plain");
    if (html.trim() !== "") {
      setSource({ kind: "html", value: html });
    } else if (text.trim() !== "") {
      setSource({ kind: "text", value: text });
    }
  }

  function onPaste(event: ClipboardEvent<HTMLDivElement>) {
    event.preventDefault();
    take(event.clipboardData);
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    take(event.dataTransfer);
  }

  const result =
    source.value.trim() === "" ? null : richTextToLatex(source.value, options);
  const pastedLength = source.kind === "html" ? source.value.length : 0;

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <p className={LABEL} id="rtl-paste-label">
          {strings.pasteLabel}
        </p>
        {/* A paste target, not an editor: typing is blocked and the pasted markup is read, never inserted. */}
        {/* biome-ignore lint/a11y/useSemanticElements: contenteditable is the paste target the clipboard's HTML needs */}
        <div
          aria-describedby="rtl-paste-hint"
          aria-labelledby="rtl-paste-label"
          aria-multiline="true"
          className="min-h-24 rounded-lg border border-border border-dashed bg-card p-4 text-muted-foreground text-sm before:content-[attr(data-placeholder)] focus:outline-none focus:ring-2 focus:ring-primary"
          contentEditable
          data-placeholder={
            pastedLength > 0
              ? tPlural(strings.pasted, locale, pastedLength, {
                  count: fmt(pastedLength),
                }).trim()
              : strings.pastePlaceholder
          }
          onBeforeInput={(event) => event.preventDefault()}
          onCut={(event) => event.preventDefault()}
          onDrop={onDrop}
          onKeyDown={(event) => {
            if (!allowKey(event)) {
              event.preventDefault();
            }
          }}
          onPaste={onPaste}
          role="textbox"
          suppressContentEditableWarning
          tabIndex={0}
        />
        <p className={HINT} id="rtl-paste-hint">
          {strings.pasteHint}
        </p>
      </div>
      <TextAreaField
        id="rtl-text"
        label={strings.textLabel}
        onChange={(value) => setSource({ kind: "text", value })}
        placeholder={strings.textPlaceholder}
        rows={6}
        value={source.kind === "text" ? source.value : ""}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() => setSource({ kind: "html", value: strings.example })}
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => setSource(NO_SOURCE)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <RichTextOptionsPanel
        onChange={setOptions}
        options={options}
        strings={strings}
      />
      <div aria-live="polite" className="space-y-4">
        {result === null ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <>
            <h2 className={WIDGET_H2}>{strings.results}</h2>
            <StatGrid>
              <StatCard
                label={strings.headings}
                value={fmt(result.stats.headings)}
              />
              <StatCard
                label={strings.paragraphs}
                value={fmt(result.stats.paragraphs)}
              />
              <StatCard
                label={strings.listItems}
                value={fmt(result.stats.listItems)}
              />
              <StatCard
                label={strings.tables}
                value={fmt(result.stats.tables)}
              />
              <StatCard label={strings.links} value={fmt(result.stats.links)} />
            </StatGrid>
            <div className="space-y-2">
              <label className={LABEL} htmlFor="rtl-output">
                {strings.codeLabel}
              </label>
              <textarea
                className={TEXTAREA}
                id="rtl-output"
                readOnly
                rows={Math.min(24, result.latex.split("\n").length + 1)}
                spellCheck={false}
                value={result.latex}
              />
            </div>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyCode}
              text={result.latex}
            />
            <h3 className={WIDGET_H3}>{strings.preambleHeading}</h3>
            {result.packages.length > 0 ? (
              <div className="space-y-2">
                <pre className={`${TEXTAREA} min-h-0`}>
                  {result.packages.join("\n")}
                </pre>
                <CopyButton
                  copiedLabel={strings.copied}
                  label={strings.copyPreamble}
                  small
                  text={result.packages.join("\n")}
                />
              </div>
            ) : (
              <Notice>{strings.noPackages}</Notice>
            )}
            <Checks result={result} strings={strings} />
          </>
        )}
      </div>
    </section>
  );
}

function Checks({
  result,
  strings,
}: {
  result: ReturnType<typeof richTextToLatex>;
  strings: typeof RICH_TEXT_TO_LATEX_STRINGS;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const { stats } = result;
  const notes = [
    [strings.imagesNote, stats.images],
    [strings.rowspanNote, stats.rowspans],
    [strings.linksNote, stats.linksDropped],
    [strings.deepNote, stats.deepLists],
  ]
    .filter(([, count]) => (count as number) > 0)
    .map(([template, count]) =>
      tPlural(template as string, locale, count as number, {
        count: fmt(count as number),
      }).trim()
    );
  if (result.nonLatin.length > 0) {
    notes.push(t(strings.nonLatinNote, { chars: result.nonLatin.join(" ") }));
  }
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.checkHeading}</h3>
      {notes.length === 0 ? (
        <Notice>{strings.allClear}</Notice>
      ) : (
        <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
    </>
  );
}
